import {
  LuExternalLink,
  LuPhone,
  LuQuote,
  LuStar,
  LuStarHalf,
} from "react-icons/lu";

import { business } from "../../content/site";

/**
 * ============================================================================
 * THIS SECTION IS INTENTIONALLY INERT UNTIL REAL REVIEWS EXIST.
 * ============================================================================
 * Mega Electric has not yet supplied any customer reviews. `business.reviews`
 * is `null` in src/content/site.js and there is no testimonial copy anywhere in
 * the content file.
 *
 * Therefore this component renders NOTHING (`return null`) today.
 *
 * PLACEHOLDER TESTIMONIALS MUST NEVER BE COMMITTED. Not "Jane D., Plano",
 * not "Great service!", not a lorem-ipsum quote, not a five-star row with no
 * data behind it. A fabricated review is a deceptive business practice (FTC
 * 16 CFR Part 465), it is a Google review-snippet policy violation, and it is
 * the fastest way to destroy the exact trust this section is meant to build.
 * If you need to see the layout, feed `items` real data in a local branch and
 * do not commit it.
 *
 * ----------------------------------------------------------------------------
 * EXPECTED DATA SHAPE
 * ----------------------------------------------------------------------------
 * Individual reviews — pass as the `items` prop, or add a `testimonials`
 * export to src/content/site.js and import it here:
 *
 *   {
 *     quote:  string   // the customer's own words, verbatim, not paraphrased
 *     author: string   // as the customer published it, e.g. "Marcus T."
 *     city:   string   // optional, e.g. "Grapevine" — only if the review itself says so
 *     rating: number   // optional, 0-5, may be fractional (4.5)
 *     source: string   // optional, e.g. "Google" — the platform it came from
 *     date:   string   // optional ISO date, e.g. "2026-03-14"
 *   }
 *
 * Aggregate — `business.reviews` in src/content/site.js:
 *
 *   {
 *     rating:   number  // e.g. 4.9
 *     count:    number  // e.g. 27
 *     platform: string  // e.g. "Google"
 *     url:      string  // deep link to the public review profile
 *   }
 *
 * Both are optional and render independently: aggregate-only, quotes-only, or
 * both. When neither is present the section does not exist.
 *
 * TODO (SEO): once real aggregate data is in place, consider AggregateRating
 * structured data — but ONLY if it mirrors the rendered numbers exactly and the
 * reviews are genuinely first-party. Do not mark up quotes copied from a
 * third-party platform as your own Review schema.
 * ============================================================================
 */

/**
 * Star rating, drawn with lucide icons and never with emoji or asterisks.
 * The visual stars are aria-hidden; the accessible name is the text equivalent
 * beside them, so a screen reader hears "4.5 out of 5 stars" exactly once.
 */
function StarRating({ rating, className = "" }) {
  const clamped = Math.max(0, Math.min(5, Number(rating) || 0));
  const full = Math.floor(clamped);
  const hasHalf = clamped - full >= 0.25 && full < 5;
  const rounded = Math.round(clamped * 10) / 10;

  return (
    <span className={`relative inline-flex items-center gap-0.5 ${className}`}>
      <span aria-hidden="true" className="inline-flex items-center gap-0.5">
        {Array.from({ length: 5 }, (_, index) => {
          if (index < full) {
            return (
              <LuStar
                key={index}
                className="h-4 w-4 shrink-0 fill-current text-ink-brand"
              />
            );
          }
          if (index === full && hasHalf) {
            return (
              <LuStarHalf
                key={index}
                className="h-4 w-4 shrink-0 fill-current text-ink-brand"
              />
            );
          }
          return (
            <LuStar key={index} className="h-4 w-4 shrink-0 text-ink-subtle" />
          );
        })}
      </span>
      <span className="sr-only">{`${rounded} out of 5 stars`}</span>
    </span>
  );
}

function TestimonialCard({ item }) {
  const meta = [item.city, item.source].filter(Boolean).join(", ");

  return (
    <li className="min-w-0">
      <figure className="card flex h-full flex-col gap-4 p-6">
        <LuQuote
          aria-hidden="true"
          className="h-6 w-6 shrink-0 text-ink-subtle"
        />

        {typeof item.rating === "number" && <StarRating rating={item.rating} />}

        <blockquote className="min-w-0">
          <p className="max-w-prose text-body text-ink [overflow-wrap:anywhere]">
            {item.quote}
          </p>
        </blockquote>

        <figcaption className="mt-auto min-w-0 pt-2">
          <span className="block text-body-sm font-semibold text-ink [overflow-wrap:anywhere]">
            {item.author}
          </span>
          {meta && (
            <span className="block text-body-sm text-ink-subtle [overflow-wrap:anywhere]">
              {meta}
            </span>
          )}
          {item.date && (
            <time
              dateTime={item.date}
              className="block text-body-sm text-ink-subtle"
            >
              {new Date(item.date).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
              })}
            </time>
          )}
        </figcaption>
      </figure>
    </li>
  );
}

const Testimonials = ({ items = [] }) => {
  const reviews = Array.isArray(items) ? items.filter(Boolean) : [];
  const aggregate =
    business.reviews &&
    typeof business.reviews.rating === "number" &&
    typeof business.reviews.count === "number"
      ? business.reviews
      : null;

  // No verified review data of any kind: render nothing at all. An empty
  // "what our customers say" heading with no customers underneath is worse
  // than having no section.
  if (!aggregate && reviews.length === 0) {
    return null;
  }

  return (
    <section
      id="testimonials"
      aria-labelledby="testimonials-heading"
      className="section bg-surface"
    >
      <div className="container-page">
        <header className="max-w-prose">
          <p className="kicker">What customers say</p>
          <h2 id="testimonials-heading" className="mt-3 text-display text-ink">
            Reviews from people we have worked for
          </h2>
        </header>

        {aggregate && (
          <div
            className="mt-8 flex min-w-0 flex-col gap-3 rounded-lg bg-surface p-6
                       shadow-sm sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex min-w-0 flex-col gap-2">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-heading-lg text-ink tabular">
                  {aggregate.rating.toFixed(1)}
                </span>
                <StarRating rating={aggregate.rating} />
              </div>
              <p className="text-body-sm text-ink-muted">
                {`Based on ${aggregate.count} ${
                  aggregate.count === 1 ? "review" : "reviews"
                }`}
                {aggregate.platform ? ` on ${aggregate.platform}` : ""}
              </p>
            </div>

            {aggregate.url && (
              <a
                href={aggregate.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-link relative inline-flex min-h-tap shrink-0
                           items-center gap-2 font-semibold"
              >
                <span>
                  {aggregate.platform
                    ? `Read them on ${aggregate.platform}`
                    : "Read the reviews"}
                </span>
                <LuExternalLink
                  aria-hidden="true"
                  className="h-4 w-4 shrink-0"
                />
                <span className="sr-only">{" (opens in a new tab)"}</span>
              </a>
            )}
          </div>
        )}

        {reviews.length > 0 && (
          <ul className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {reviews.map((item, index) => (
              <TestimonialCard
                key={`${item.author}-${item.date || index}`}
                item={item}
              />
            ))}
          </ul>
        )}

        {/* Research finding: testimonials convert best sitting next to the
            call to action, not siloed on a reviews page. */}
        <div
          className="mt-10 flex flex-col items-start gap-4 rounded-lg bg-surface
                     p-6 shadow-sm sm:flex-row sm:items-center sm:justify-between"
        >
          <p className="max-w-prose text-body text-ink">
            Ready for the same treatment? Tell us what is going on and we will
            take it from there.
          </p>
          <a
            href={business.phone.href}
            className="btn-urgent w-full shrink-0 sm:w-auto"
          >
            <LuPhone aria-hidden="true" className="h-5 w-5 shrink-0" />
            <span className="tabular">Call {business.phone.display}</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
